import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { applyLang, getCurrentLang } from '../utils/i18n';
import { eventsApi, EVENT_CATEGORIES, CATEGORY_LABEL, RECURRENCE_LABEL } from '../services/eventsApi';
import './Agenda.css';

const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sept', 'Oct', 'Nov', 'Déc'];

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateRange(start, end) {
  if (!start) return '';
  const s = new Date(start);
  if (!end) return `${s.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} · ${fmtTime(start)}`;
  const e = new Date(end);
  const sameDay = s.toDateString() === e.toDateString();
  if (sameDay) {
    return `${s.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })} · ${fmtTime(start)} → ${fmtTime(end)}`;
  }
  return `${s.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → ${e.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}`;
}

const ProgrammeNiane = () => {
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.agd-page .reveal').forEach((el) => io.observe(el));
    applyLang(getCurrentLang());
    return () => io.disconnect();
  });

  useEffect(() => {
    let cancelled = false;
    eventsApi.list({ limit: 100 })
      .then((data) => { if (!cancelled) { setEvents(data.items); setLoading(false); } })
      .catch(() => { if (!cancelled) { setEvents([]); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const c = { all: events.length };
    for (const e of events) c[e.category] = (c[e.category] || 0) + 1;
    return c;
  }, [events]);

  const featured = useMemo(() => events.filter((e) => e.featured).slice(0, 2), [events]);

  const visible = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => e.category === filter);
  }, [filter, events]);

  const nextNiani = useMemo(() => events.find((e) => e.slug === 'niani-grand-appel'), [events]);
  const religious = counts.religieux || 0;
  const cultural = counts.culturel || 0;

  const imgFor = (e) => e.imageUrl || `https://picsum.photos/seed/${e.imageSeed || `cb-evt-${e.id}`}/1400/900`;

  return (
    <main className="agd-page">
      {/* ============== HERO ============== */}
      <header className="agd-hero">
        <div className="agd-hero-bg" />
        <div className="agd-hero-grain" />
        <div className="agd-hero-watermark" aria-hidden="true">Agenda</div>

        <div className="wrap agd-hero-inner reveal">
          <span className="agd-hero-eyebrow">Cambérène · Agenda du village</span>
          <h1>
            Le village,<br />
            <span className="ital">à l'agenda.</span>
          </h1>
          <p className="lede">
            Niani, Mawlid, Festival tassou, matchs de l'ASC, séances du conseil municipal — toutes
            les célébrations religieuses, culturelles, sportives et communautaires de Cambérène,
            classées chronologiquement.
          </p>

          <div className="agd-hero-foot">
            <div><b>{loading ? '—' : events.length}</b><span>Événements à venir</span></div>
            <div><b>{religious}</b><span>Religieux</span></div>
            <div><b>{cultural}</b><span>Culturels</span></div>
            <div><b>{nextNiani ? new Date(nextNiani.startDate).getFullYear() : '—'}</b><span>Prochain Niani</span></div>
          </div>
        </div>
      </header>

      {/* ============== FILTRES ============== */}
      <div className="agd-filters">
        <div className="wrap">
          <span className="lab">Catégories —</span>
          {EVENT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`agd-chip${filter === c.id ? ' on' : ''}`}
              onClick={() => setFilter(c.id)}
            >
              {c.lbl}
              <span className="ct">{counts[c.id] || 0}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ============== À LA UNE ============== */}
      {featured.length > 0 && (
        <section className="agd-featured">
          <div className="wrap">
            <div className="agd-head reveal">
              <div>
                <span className="eyebrow">À ne pas manquer</span>
                <h2>Les rendez-vous<br /><span className="ital">phares.</span></h2>
              </div>
            </div>

            <div className="fea-grid">
              {featured.map((e) => (
                <article key={e.id} className="fea-event reveal">
                  <div className="img" style={{ backgroundImage: `url('${imgFor(e)}')` }} />
                  <div className="grad" />
                  <div className="top">
                    <span className="cat">{CATEGORY_LABEL[e.category] || e.category}</span>
                    <span className="rec">{RECURRENCE_LABEL[e.recurrence] || e.recurrence}</span>
                  </div>
                  <div className="body">
                    <div className="date">
                      {e.isOngoing && <span className="live">En cours</span>}
                      <span>{fmtDateRange(e.startDate, e.endDate)}</span>
                    </div>
                    <h3>{e.title}</h3>
                    {e.summary && <p>{e.summary}</p>}
                    <div className="meta">
                      {e.location && <span>📍 {e.location}</span>}
                      {e.organizer && <span>Org. · {e.organizer}</span>}
                      {e.price && <span>{e.price}</span>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============== LISTE COMPLÈTE ============== */}
      <section className="agd-list">
        <div className="wrap">
          <div className="agd-head reveal">
            <div>
              <span className="eyebrow">
                {filter === 'all' ? 'Tous les événements à venir' : CATEGORY_LABEL[filter]}
                {' · '}
                {visible.length} événement{visible.length > 1 ? 's' : ''}
              </span>
              <h2>Calendrier <span className="ital">complet.</span></h2>
            </div>
          </div>

          {loading && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)', fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.18em', textTransform: 'uppercase' }}>
              Chargement de l'agenda…
            </p>
          )}

          {!loading && visible.length === 0 && (
            <p style={{ textAlign: 'center', padding: '40px 0', color: 'var(--char-500)' }}>
              Aucun événement {filter !== 'all' ? `dans la catégorie « ${CATEGORY_LABEL[filter]} »` : ''} à venir.
            </p>
          )}

          {!loading && visible.length > 0 && (
            <div className="evt-list">
              {visible.map((e) => {
                const start = new Date(e.startDate);
                const hasEnd = !!e.endDate && new Date(e.endDate).toDateString() !== start.toDateString();
                return (
                  <article
                    key={e.id}
                    className={`evt-row${e.featured ? ' featured' : ''}${e.isPast ? ' past' : ''} reveal`}
                  >
                    <div className="date">
                      <b>{start.getDate().toString().padStart(2, '0')}</b>
                      <small>{MONTHS_SHORT[start.getMonth()]} {start.getFullYear()}</small>
                      {hasEnd && <span className="multi">→ {new Date(e.endDate).getDate()} {MONTHS_SHORT[new Date(e.endDate).getMonth()]}</span>}
                    </div>
                    <div className="info">
                      <div className="badges">
                        <span className={`cat ${e.category}`}>{CATEGORY_LABEL[e.category] || e.category}</span>
                        {e.recurrence !== 'unique' && <span className="rec">↻ {RECURRENCE_LABEL[e.recurrence]}</span>}
                        {e.isOngoing && <span className="live">● en cours</span>}
                        {e.featured && <span className="rec" style={{ color: 'var(--gold)', borderColor: 'rgba(201,169,97,.45)' }}>★ À la une</span>}
                      </div>
                      <h4>{e.title}</h4>
                      {e.summary && <p className="summary">{e.summary}</p>}
                      <div className="meta">
                        {e.location && <span>📍 <b>{e.location}</b></span>}
                        {!hasEnd && fmtTime(e.startDate) !== '00:00' && (
                          <span>{fmtTime(e.startDate)}{e.endDate ? ` → ${fmtTime(e.endDate)}` : ''}</span>
                        )}
                        {e.price && <span>{e.price}</span>}
                        {e.organizer && <span>Org. · {e.organizer}</span>}
                      </div>
                    </div>
                    <div className="act">
                      {e.registrationUrl ? (
                        <a href={e.registrationUrl} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()}>
                          S'inscrire →
                        </a>
                      ) : (
                        <span>Détails →</span>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ============== CTA ============== */}
      <section className="agd-cta">
        <div className="wrap reveal">
          <span className="eyebrow">Proposer un événement</span>
          <h3>Vous organisez quelque chose <span className="ital">à Cambérène ?</span></h3>
          <p>
            Concert, cérémonie, tournoi, atelier — soumettez votre événement, il sera vérifié
            puis publié sur l'agenda du village.
          </p>
          <div className="agd-cta-actions">
            <Link to="/inscription" className="btn btn-primary">
              <span>Soumettre un événement</span>
              <span className="arrow">→</span>
            </Link>
            <Link to="/actualites" className="btn btn-ghost">
              <span>Les actualités</span>
              <span className="arrow">↗</span>
            </Link>
            <Link to="/mairie" className="btn btn-ghost">
              <span>La Mairie</span>
              <span className="arrow">↗</span>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProgrammeNiane;
